// Backs the global "new version / what's new" banner in App.vue.
//
// - Release notes live in src/data/release-notes.json and are bundled into
//   the JS at build time, so the notes a user sees always match the code
//   they are running (no separate file to forget to deploy). That file is
//   CUSTOMER-FACING - every string in it is shown verbatim on
//   portal.vozcare.com / atdc.vozcare.com. Authoring rules are in
//   CONTRIBUTING.md; scripts/generate-build-info.js rejects malformed or
//   developer-sounding entries at build time.
// - build-info.json (version, short commit, build time) is generated on
//   every build by scripts/generate-build-info.js and fetched at runtime,
//   both to detect "a newer build is live" and to show the current build
//   in the release notes dialog. No backend/AppSync involved.
import releaseNotes from "@/data/release-notes.json";

// Versioned prefix: the old "vozcare_admin_last_seen_release_version" key
// stored a hand-typed version ("1.0.0") and is intentionally ignored.
const DISMISSED_KEY_PREFIX = "vozcare_admin_release_notes_dismissed:";

function cacheBustedFetch(url) {
  return fetch(`${url}?_=${Date.now()}`, { cache: "no-store" }).then((r) => {
    if (!r.ok) throw new Error(`${url} responded ${r.status}`);
    return r.json();
  });
}

export function fetchBuildInfo() {
  return cacheBustedFetch("/build-info.json");
}

// Newest first (enforced at build time).
export function getReleaseNotes() {
  return Array.isArray(releaseNotes) ? releaseNotes : [];
}

// Dismissals are remembered per browser. They used to be per signed-in
// user, but the banner also shows on the sign-in page (no user yet, so it
// was saved as "anonymous") - closing it there and then logging in showed
// the same notes again, which users reported as "the same release notes
// every time". The newest id dismissed under ANY key (old per-user keys
// included) counts, so nobody sees an already-closed note again.
const DISMISSED_KEY = DISMISSED_KEY_PREFIX + "browser";

export async function getDismissedReleaseId() {
  try {
    let newest = null;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(DISMISSED_KEY_PREFIX)) continue;
      const id = localStorage.getItem(key);
      if (id && (!newest || id > newest)) newest = id;
    }
    return newest;
  } catch (e) {
    return null;
  }
}

export async function setDismissedReleaseId(id) {
  try {
    localStorage.setItem(DISMISSED_KEY, id);
  } catch (e) {
    // Private-browsing / storage-disabled - the "what's new" banner will
    // just reappear next load, which is a harmless degrade.
  }
}

// Ids are YYYY-MM-DD(.N), so plain string comparison orders them.
export function isNewerThan(id, dismissedId) {
  return !!id && (!dismissedId || id > dismissedId);
}

// ---- Forced upgrade ----------------------------------------------------
// The commit this bundle was built from (baked in by vue.config.js). "dev"
// means unknown (local `vue-cli-service` run), which disables the check.
/* global __VOZCARE_BUILD_COMMIT__ */
export const BUNDLE_COMMIT =
  typeof __VOZCARE_BUILD_COMMIT__ !== "undefined" ? __VOZCARE_BUILD_COMMIT__ : "dev";

// Set once a newer build is known to be live. The router checks it so the
// next in-app navigation becomes a full page load of the new build.
let updatePending = false;

export function markUpdatePending() {
  updatePending = true;
}

export function isUpdatePending() {
  return updatePending;
}

// Reload loop guard: try an automatic reload at most once per target
// commit per tab. If the browser still serves the old build after that
// (e.g. an aggressively cached index.html), the caller falls back to a
// blocking "please reload" dialog instead of reloading forever.
export function tryAutoReloadTo(commit) {
  const key = "vozcare_force_upgrade_reloaded:" + commit;
  try {
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, "1");
  } catch (e) {
    return false;
  }
  window.location.reload();
  return true;
}
